import { clamp01 } from './math';

/**
 * Colour helpers.
 *
 * Interpolation happens in OKLab rather than sRGB: mixing #d8ff3e with #2b6cff in sRGB
 * runs through a muddy grey, while OKLab keeps the perceived lightness and chroma even
 * across the whole ramp. The full palette system builds on these primitives in stage 4.
 *
 * Conversions follow Björn Ottosson's OKLab definition (https://bottosson.github.io/posts/oklab/).
 */

export interface Rgb {
  r: number;
  g: number;
  b: number;
}

export function parseHex(hex: string): Rgb {
  const clean = hex.trim().replace('#', '');
  const full =
    clean.length === 3
      ? clean
          .split('')
          .map((c) => c + c)
          .join('')
      : clean.padEnd(6, '0').slice(0, 6);
  const n = Number.parseInt(full, 16);
  return { r: ((n >> 16) & 255) / 255, g: ((n >> 8) & 255) / 255, b: (n & 255) / 255 };
}

export function toHex({ r, g, b }: Rgb): string {
  const c = (v: number): string =>
    Math.round(clamp01(v) * 255)
      .toString(16)
      .padStart(2, '0');
  return `#${c(r)}${c(g)}${c(b)}`;
}

export function rgbaString({ r, g, b }: Rgb, alpha: number): string {
  return `rgba(${Math.round(clamp01(r) * 255)},${Math.round(clamp01(g) * 255)},${Math.round(clamp01(b) * 255)},${clamp01(alpha).toFixed(3)})`;
}

const srgbToLinear = (c: number): number => (c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4);
const linearToSrgb = (c: number): number => (c <= 0.0031308 ? c * 12.92 : 1.055 * c ** (1 / 2.4) - 0.055);

export interface Oklab {
  L: number;
  a: number;
  b: number;
}

export function rgbToOklab({ r, g, b }: Rgb): Oklab {
  const lr = srgbToLinear(r);
  const lg = srgbToLinear(g);
  const lb = srgbToLinear(b);

  const l = Math.cbrt(0.4122214708 * lr + 0.5363325363 * lg + 0.0514459929 * lb);
  const m = Math.cbrt(0.2119034982 * lr + 0.6806995451 * lg + 0.1073969566 * lb);
  const s = Math.cbrt(0.0883024619 * lr + 0.2817188376 * lg + 0.6299787005 * lb);

  return {
    L: 0.2104542553 * l + 0.793617785 * m - 0.0040720468 * s,
    a: 1.9779984951 * l - 2.428592205 * m + 0.4505937099 * s,
    b: 0.0259040371 * l + 0.7827717662 * m - 0.808675766 * s,
  };
}

export function oklabToRgb({ L, a, b }: Oklab): Rgb {
  const l = (L + 0.3963377774 * a + 0.2158037573 * b) ** 3;
  const m = (L - 0.1055613458 * a - 0.0638541728 * b) ** 3;
  const s = (L - 0.0894841775 * a - 1.291485548 * b) ** 3;

  return {
    r: clamp01(linearToSrgb(4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s)),
    g: clamp01(linearToSrgb(-1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s)),
    b: clamp01(linearToSrgb(-0.0041960863 * l - 0.7034186147 * m + 1.707614701 * s)),
  };
}

// ── OKLCH ────────────────────────────────────────────────────────────────────
//
// The polar form of OKLab: lightness, chroma, hue. Interpolating there keeps the
// saturation constant across a ramp instead of dipping through grey in the middle,
// which is what makes a two-colour gradient look dull in RGB.

export interface Oklch {
  L: number;
  C: number;
  /** Hue in radians. */
  h: number;
}

export function oklabToOklch({ L, a, b }: Oklab): Oklch {
  return { L, C: Math.hypot(a, b), h: Math.atan2(b, a) };
}

export function oklchToOklab({ L, C, h }: Oklch): Oklab {
  return { L, a: Math.cos(h) * C, b: Math.sin(h) * C };
}

export function rgbToOklch(rgb: Rgb): Oklch {
  return oklabToOklch(rgbToOklab(rgb));
}

export function oklchToRgb(lch: Oklch): Rgb {
  return oklabToRgb(oklchToOklab(lch));
}

/** Mixes in OKLCH, taking the shorter way around the hue circle. */
export function mixOklch(a: Rgb, b: Rgb, t: number): Rgb {
  const ca = rgbToOklch(a);
  const cb = rgbToOklch(b);
  const k = clamp01(t);
  // A greyscale endpoint has no meaningful hue; borrow the other one's so the ramp
  // does not swing through an arbitrary direction on its way out of grey.
  const ha = ca.C < 1e-4 ? cb.h : ca.h;
  const hb = cb.C < 1e-4 ? ca.h : cb.h;
  let dh = ((hb - ha + Math.PI * 3) % (Math.PI * 2)) - Math.PI;
  return oklchToRgb({
    L: ca.L + (cb.L - ca.L) * k,
    C: ca.C + (cb.C - ca.C) * k,
    h: ha + dh * k,
  });
}

// ── HSL ──────────────────────────────────────────────────────────────────────

export interface Hsl {
  h: number;
  s: number;
  l: number;
}

export function rgbToHsl({ r, g, b }: Rgb): Hsl {
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  const d = max - min;
  if (d < 1e-9) return { h: 0, s: 0, l };
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h: number;
  if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
  else if (max === g) h = ((b - r) / d + 2) / 6;
  else h = ((r - g) / d + 4) / 6;
  return { h, s, l };
}

export function hslToRgb({ h, s, l }: Hsl): Rgb {
  if (s <= 0) return { r: l, g: l, b: l };
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  const channel = (v: number): number => {
    let t = v;
    if (t < 0) t += 1;
    if (t > 1) t -= 1;
    if (t < 1 / 6) return p + (q - p) * 6 * t;
    if (t < 1 / 2) return q;
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
    return p;
  };
  return { r: channel(h + 1 / 3), g: channel(h), b: channel(h - 1 / 3) };
}

export function mixHsl(a: Rgb, b: Rgb, t: number): Rgb {
  const ha = rgbToHsl(a);
  const hb = rgbToHsl(b);
  const k = clamp01(t);
  const dh = ((hb.h - ha.h + 1.5) % 1) - 0.5;
  return hslToRgb({ h: (ha.h + dh * k + 1) % 1, s: ha.s + (hb.s - ha.s) * k, l: ha.l + (hb.l - ha.l) * k });
}

export function mixRgb(a: Rgb, b: Rgb, t: number): Rgb {
  const k = clamp01(t);
  return { r: a.r + (b.r - a.r) * k, g: a.g + (b.g - a.g) * k, b: a.b + (b.b - a.b) * k };
}

export type ColorSpace = 'rgb' | 'hsl' | 'oklch';

export function mixIn(space: ColorSpace, a: Rgb, b: Rgb, t: number): Rgb {
  if (space === 'rgb') return mixRgb(a, b, t);
  if (space === 'hsl') return mixHsl(a, b, t);
  return mixOklch(a, b, t);
}

/** Perceptually even mix of two colours. */
export function mixOklab(a: Rgb, b: Rgb, t: number): Rgb {
  const la = rgbToOklab(a);
  const lb = rgbToOklab(b);
  const k = clamp01(t);
  return oklabToRgb({
    L: la.L + (lb.L - la.L) * k,
    a: la.a + (lb.a - la.a) * k,
    b: la.b + (lb.b - la.b) * k,
  });
}

/**
 * Pre-computes a colour ramp as packed RGB bytes.
 *
 * Techniques colour hundreds of thousands of elements per frame; doing an OKLab
 * round-trip per element would dominate the frame time, and building `rgba(...)`
 * strings would allocate just as heavily. Sampling a baked table is a couple of array
 * reads.
 */
export function bakeRamp(from: string, to: string, steps = 256): Uint8Array {
  const a = parseHex(from);
  const b = parseHex(to);
  const table = new Uint8Array(steps * 3);
  for (let i = 0; i < steps; i++) {
    const { r, g, b: bb } = mixOklab(a, b, i / (steps - 1));
    table[i * 3] = Math.round(r * 255);
    table[i * 3 + 1] = Math.round(g * 255);
    table[i * 3 + 2] = Math.round(bb * 255);
  }
  return table;
}

/** Builds a canvas colour string from a baked ramp. */
export function rampColor(table: Uint8Array, t: number, alpha: number): string {
  const steps = table.length / 3;
  const i = Math.min(steps - 1, Math.max(0, Math.round(clamp01(t) * (steps - 1)))) * 3;
  return `rgba(${table[i]},${table[i + 1]},${table[i + 2]},${alpha.toFixed(3)})`;
}
